import { ethers } from 'ethers';
import { 
  EXPORT_ASSISTANT_ABI
} from '../contracts/abi';
import { getCounterstakeABI, createCounterstakeContract as createCounterstakeContractFactory, createAssistantFactoryContract } from './contract-factory.js';
import { getBridgeInstanceByAddress } from '../config/networks';
import { safeBigNumberToInt } from './safe-reward-handler';
import { fetchClaimDetails } from './claim-details-fetcher.js';

// 3DPass Bridge Contract Utilities


/**
 * Get the appropriate ABI for a network
 * @param {string} networkSymbol - Network symbol (ETH, BSC, 3DPass)
 * @param {string} contractType - 'export' or 'import_wrapper'
 * @returns {Array} Contract ABI
 */
export const getContractABI = (networkSymbol, contractType) => {
  // Always use the actual Counterstake ABI since all bridges use the same Counterstake contract
  return getCounterstakeABI();
};

/**
 * Get bridge instance by address
 * @param {string} address - Bridge contract address
 * @returns {Object|null} Bridge instance or null if not found
 */
export const getBridgeInstance = (address) => {
  return getBridgeInstanceByAddress(address);
};

/**
 * Get bridge ABI based on bridge instance
 * @param {Object} bridgeInstance - Bridge instance from BRIDGE_INSTANCES
 * @returns {Array} Contract ABI
 */
export const getBridgeABI = (bridgeInstance) => {
  if (!bridgeInstance) return null;
  
  // Always use the actual Counterstake ABI since all bridges use the same Counterstake contract
  return getCounterstakeABI();
};

/**
 * Create bridge contract instance
 * @param {ethers.providers.Provider|ethers.Signer} providerOrSigner - Provider or signer
 * @param {string} contractAddress - Contract address
 * @param {string} networkSymbol - Network symbol
 * @param {string} contractType - 'export' or 'import_wrapper'
 * @returns {ethers.Contract} Contract instance
 */
export const createBridgeContract = (providerOrSigner, contractAddress, networkSymbol, contractType) => {
  // Always use the actual Counterstake ABI since all bridges use the same Counterstake contract
  return createCounterstakeContractFactory(contractAddress, providerOrSigner);
};

/**
 * Get required stake for a transfer
 * @param {ethers.Contract} contract - Bridge contract instance
 * @param {string} amount - Transfer amount
 * @param {number} transferTokenDecimals - Transfer token decimals
 * @param {number} stakeTokenDecimals - Stake token decimals
 * @returns {Promise<string>} Required stake amount
 */
export const getRequiredStake = async (contract, amount, transferTokenDecimals = 18, stakeTokenDecimals = 18) => {
  try {
    const amountWei = ethers.utils.parseUnits(amount, transferTokenDecimals);
    const requiredStakeWei = await contract.getRequiredStake(amountWei);
    return ethers.utils.formatUnits(requiredStakeWei, stakeTokenDecimals);
  } catch (error) {
    console.error('Error getting required stake:', error);
    throw new Error(`Failed to get required stake: ${error.message}`);
  }
};

/**
 * Get required stake in original token units (not P3D)
 * @param {ethers.Contract} contract - Bridge contract instance
 * @param {string} amount - Transfer amount
 * @param {number} transferTokenDecimals - Transfer token decimals
 * @returns {Promise<string>} Required stake amount in original token units
 */
export const getRequiredStakeInOriginalToken = async (contract, amount, transferTokenDecimals = 18) => {
  try {
    const amountWei = ethers.utils.parseUnits(amount, transferTokenDecimals);
    const requiredStakeWei = await contract.getRequiredStake(amountWei);
    
    // Convert stake from stake token to original token units
    // The stake is returned in P3D units, but we want to show it in original token units
    // So we format it using the original token decimals
    return ethers.utils.formatUnits(requiredStakeWei, transferTokenDecimals);
  } catch (error) {
    console.error('Error getting required stake in original token:', error);
    throw new Error(`Failed to get required stake in original token: ${error.message}`);
  }
};

/**
 * Transfer tokens to foreign chain (Export)
 * @param {ethers.Contract} contract - Bridge contract instance
 * @param {string} foreignAddress - Foreign address
 * @param {string} data - Additional data
 * @param {string} amount - Transfer amount
 * @param {string} reward - Reward amount (default: '0')
 * @param {number} decimals - Token decimals (default: 18)
 * @param {boolean} isPrecompile - Whether token is a precompile (default: false)
 * @returns {Promise<ethers.ContractReceipt>} Transaction receipt
 */
export const transferToForeignChain = async (
  contract, 
  foreignAddress, 
  data, 
  amount, 
  reward = '0', 
  decimals = 18,
  isPrecompile = false
) => {
  try {
    const amountWei = ethers.utils.parseUnits(amount, decimals);
    const rewardWei = ethers.utils.parseUnits(reward, decimals);
    
    // For precompile tokens, we need to handle differently
    if (isPrecompile) {
      // Precompile tokens are handled natively by the bridge
      const tx = await contract.transferToForeignChain(foreignAddress, data, amountWei, rewardWei);
      return await tx.wait();
    } else {
      // Regular ERC20 tokens
      const tx = await contract.transferToForeignChain(foreignAddress, data, amountWei, rewardWei);
      return await tx.wait();
    }
  } catch (error) {
    console.error('Error transferring to foreign chain:', error);
    throw new Error(`Failed to transfer to foreign chain: ${error.message}`);
  }
};

/**
 * Transfer tokens to home chain (Import Wrapper)
 * @param {ethers.Contract} contract - Bridge contract instance
 * @param {string} homeAddress - Home address
 * @param {string} data - Additional data
 * @param {string} amount - Transfer amount
 * @param {string} reward - Reward amount (default: '0')
 * @param {number} decimals - Token decimals (default: 18)
 * @returns {Promise<ethers.ContractReceipt>} Transaction receipt
 */
export const transferToHomeChain = async (
  contract, 
  homeAddress, 
  data, 
  amount, 
  reward = '0', 
  decimals = 18
) => {
  try {
    const amountWei = ethers.utils.parseUnits(amount, decimals);
    const rewardWei = ethers.utils.parseUnits(reward, decimals);
    
    const tx = await contract.transferToHomeChain(homeAddress, data, amountWei, rewardWei);
    return await tx.wait();
  } catch (error) {
    console.error('Error transferring to home chain:', error);
    throw new Error(`Failed to transfer to home chain: ${error.message}`);
  }
};

/**
 * Get bridge settings
 * @param {ethers.Contract} contract - Bridge contract instance
 * @returns {Promise<Object>} Bridge settings
 */
export const getBridgeSettings = async (contract) => {
  try {
    const settings = await contract.settings();
    return {
      tokenAddress: settings.tokenAddress,
      ratio100: settings.ratio100,
      counterstake_coef100: settings.counterstake_coef100,
      min_tx_age: settings.min_tx_age,
      min_stake: settings.min_stake,
      challenging_periods: settings.challenging_periods,
      large_challenging_periods: settings.large_challenging_periods,
      large_threshold: settings.large_threshold,
    };
  } catch (error) {
    console.error('Error getting bridge settings:', error);
    throw new Error(`Failed to get bridge settings: ${error.message}`);
  }
};

/**
 * Check if contract is a 3DPass contract
 * @param {ethers.Contract} contract - Contract instance
 * @returns {Promise<boolean>} True if 3DPass contract
 */
export const is3DPassContract = async (contract) => {
  try {
    // Check if contract has P3D_PRECOMPILE function (3DPass specific)
    await contract.P3D_PRECOMPILE();
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get bridge token information
 * @param {ethers.Contract} contract - Bridge contract instance
 * @param {ethers.providers.Provider} provider - Web3 provider
 * @returns {Promise<Object>} Token information
 */
export const getBridgeTokenInfo = async (contract, provider) => {
  try {
    const settings = await getBridgeSettings(contract);
    const tokenAddress = settings.tokenAddress;
    
    // Check if it's a 3DPass precompile
    const is3DPass = await is3DPassContract(contract);
    
    if (is3DPass) {
      // For 3DPass precompiles, use the appropriate ABI
      const { get3DPassTokenMetadata } = await import('./threedpass');
      return await get3DPassTokenMetadata(provider, tokenAddress);
    } else {
      // For regular ERC20 tokens
      const tokenContract = new ethers.Contract(
        tokenAddress,
        ['function name() view returns (string)', 'function symbol() view returns (string)', 'function decimals() view returns (uint8)'],
        provider
      );
      
      const [name, symbol, decimals] = await Promise.all([
        tokenContract.name(),
        tokenContract.symbol(),
        tokenContract.decimals()
      ]);
      
      return {
        name,
        symbol,
        decimals,
        address: tokenAddress,
        isPrecompile: false
      };
    }
  } catch (error) {
    console.error('Error getting bridge token info:', error);
    throw new Error(`Failed to get bridge token info: ${error.message}`);
  }
};

/**
 * Validate bridge transfer parameters
 * @param {Object} params - Transfer parameters
 * @param {ethers.Contract} contract - Bridge contract instance
 * @returns {Promise<Object>} Validation result
 */
export const validateBridgeTransfer = async (params, contract) => {
  const { amount, recipient, data = '' } = params;
  const errors = [];
    
    // Validate amount
    if (!amount || parseFloat(amount) <= 0) {
      errors.push('Invalid amount');
    }
    
  // Validate recipient address
  if (!recipient || !ethers.utils.isAddress(recipient)) {
    errors.push('Invalid recipient address');
  }

  // Validate data (optional)
  if (data && typeof data !== 'string') {
    errors.push('Invalid data format');
  }

  // Check if contract is valid
  try {
    await contract.settings();
  } catch (error) {
    errors.push('Invalid bridge contract');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Get pending transfers for a user
 * @param {ethers.Contract} exportContract - Export bridge contract
 * @param {ethers.Contract} importContract - Import bridge contract
 * @param {string} userAddress - User address
 * @returns {Promise<Array>} Array of pending transfers
 */
export const getPendingTransfers = async (exportContract, importContract, userAddress) => {
  try {
    const pendingTransfers = [];
    
    // Get export transfers (outgoing)
    if (exportContract) {
      try {
    const exportFilter = exportContract.filters.NewExpatriation(userAddress);
    const exportEvents = await exportContract.queryFilter(exportFilter);
    
    for (const event of exportEvents) {
      const { sender_address, amount, reward, foreign_address, data } = event.args;
      const blockNumber = event.blockNumber;
      
      // Check if transfer is still pending (not claimed yet)
      const isClaimed = await checkIfTransferClaimed(exportContract, event.transactionHash);
      
      if (!isClaimed) {
        pendingTransfers.push({
          id: event.transactionHash,
          type: 'export',
          sender: sender_address,
          amount: ethers.utils.formatEther(amount),
          reward: ethers.utils.formatEther(reward),
          destinationAddress: foreign_address,
          data: data,
          blockNumber,
          timestamp: await getBlockTimestamp(exportContract.provider, blockNumber),
          status: 'pending',
          contract: exportContract,
        });
          }
        }
      } catch (error) {
        console.warn('Error getting export transfers:', error);
      }
    }
    
    // Get import transfers (incoming)
    if (importContract) {
      try {
    const importFilter = importContract.filters.NewRepatriation(userAddress);
    const importEvents = await importContract.queryFilter(importFilter);
    
    for (const event of importEvents) {
      const { sender_address, amount, reward, home_address, data } = event.args;
      const blockNumber = event.blockNumber;
      
      // Check if transfer is still pending (not claimed yet)
      const isClaimed = await checkIfTransferClaimed(importContract, event.transactionHash);
      
      if (!isClaimed) {
        pendingTransfers.push({
          id: event.transactionHash,
          type: 'import',
          sender: sender_address,
          amount: ethers.utils.formatEther(amount),
          reward: ethers.utils.formatEther(reward),
          destinationAddress: home_address,
          data: data,
          blockNumber,
          timestamp: await getBlockTimestamp(importContract.provider, blockNumber),
          status: 'pending',
          contract: importContract,
        });
          }
        }
      } catch (error) {
        console.warn('Error getting import transfers:', error);
      }
    }
    
    return pendingTransfers.sort((a, b) => b.timestamp - a.timestamp);
  } catch (error) {
    console.error('Error getting pending transfers:', error);
    throw new Error(`Failed to get pending transfers: ${error.message}`);
  }
};

/**
 * Check if a transfer has been claimed
 * @param {ethers.Contract} contract - Bridge contract instance
 * @param {string} transferHash - Transfer hash
 * @returns {Promise<boolean>} True if claimed
 */
export const checkIfTransferClaimed = async (contract, transferHash) => {
  try {
    // This is a simplified check - in a real implementation, you'd need to
    // check the actual claim events or contract state
    const claimFilter = contract.filters.Claimed();
    const claimEvents = await contract.queryFilter(claimFilter);
    
    // Check if any claim event references this transfer
    for (const event of claimEvents) {
      if (event.transactionHash === transferHash) {
        return true;
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking if transfer claimed:', error);
    return false;
  }
};

/**
 * Get block timestamp
 * @param {ethers.providers.Provider} provider - Web3 provider
 * @param {number} blockNumber - Block number
 * @returns {Promise<number>} Block timestamp
 */
export const getBlockTimestamp = async (provider, blockNumber) => {
  try {
    console.log(`🔍 Getting block timestamp for block ${blockNumber}`);
    const block = await provider.getBlock(blockNumber);
    
    console.log(`🔍 Block ${blockNumber} timestamp: ${block.timestamp} (${new Date(block.timestamp * 1000).toISOString()})`);
    
    return block.timestamp;
  } catch (error) {
    console.error(`❌ Error getting block timestamp for block ${blockNumber}:`, error);
    throw new Error(`Cannot get timestamp for block ${blockNumber}: ${error.message}`);
  }
};

/**
 * Claim a transfer using assistant contract
 * @param {ethers.Contract} assistantContract - Assistant contract instance
 * @param {Object} transfer - Transfer object
 * @param {string} claimerAddress - Claimer address
 * @param {string} tokenSymbol - Token symbol for error messages (default: 'tokens')
 * @returns {Promise<ethers.ContractReceipt>} Transaction receipt
 */
export const claimTransfer = async (assistantContract, transfer, claimerAddress, tokenSymbol = 'tokens', decimals = 18) => {
  try {
    const { destinationAddress, data, amount, reward } = transfer;
    const amountWei = ethers.utils.parseEther(amount);
    const rewardWei = ethers.utils.parseEther(reward);
    
    // CRITICAL: Reward should be passed as int, not BigNumber for assistant claim function
    // The ABI expects "int reward", not "uint reward" - convert to integer safely using centralized utility
    const rewardInt = safeBigNumberToInt(rewardWei, tokenSymbol, decimals);
    
    let tx;
    
    if (transfer.type === 'export') {
      // Claim export transfer
      tx = await assistantContract.claim(destinationAddress, data, amountWei, rewardInt);
    } else {
      // Claim import transfer
      tx = await assistantContract.claim(destinationAddress, data, amountWei, rewardInt);
    }
    
    return await tx.wait();
  } catch (error) {
    console.error('Error claiming transfer:', error);
    throw new Error(`Failed to claim transfer: ${error.message}`);
  }
};

/**
 * Get assistant contract for a bridge
 * @param {ethers.Contract} bridgeContract - Bridge contract instance
 * @param {ethers.providers.Provider} provider - Web3 provider
 * @param {string} assistantFactoryAddress - Assistant factory address
 * @returns {Promise<ethers.Contract|null>} Assistant contract or null
 */
export const getAssistantContract = async (bridgeContract, provider, assistantFactoryAddress) => {
  try {
    // Use the centralized factory contract creation
    const factoryContract = createAssistantFactoryContract(assistantFactoryAddress, provider);
    
    const bridgeAddress = bridgeContract.address;
    let assistantAddress;
    
    // Try to get export assistant first
    try {
      assistantAddress = await factoryContract.getExportAssistant(bridgeAddress);
    } catch (error) {
      // If not found, try import assistant
      try {
        assistantAddress = await factoryContract.getImportAssistant(bridgeAddress);
      } catch (error2) {
        return null;
      }
    }
    
    if (assistantAddress === ethers.constants.AddressZero) {
      return null;
    }
    
    // Determine which ABI to use based on the bridge contract
    const is3DPass = await is3DPassContract(bridgeContract);
    const abi = is3DPass ? EXPORT_ASSISTANT_ABI : EXPORT_ASSISTANT_ABI; // Use same ABI for now
    
    return new ethers.Contract(assistantAddress, abi, provider);
  } catch (error) {
    console.error('Error getting assistant contract:', error);
    return null;
  }
};

/**
 * Get transfer status
 * @param {ethers.Contract} contract - Bridge contract instance
 * @param {string} transferHash - Transfer hash
 * @returns {Promise<Object>} Transfer status
 */
export const getTransferStatus = async (contract, transferHash) => {
  try {
    const receipt = await contract.provider.getTransactionReceipt(transferHash);
    
    if (!receipt) {
      return { status: 'pending', confirmed: false };
    }
    
    if (receipt.status === 0) {
      return { status: 'failed', confirmed: true, error: 'Transaction failed' };
    }
    
    // Check if transfer has been claimed
    const isClaimed = await checkIfTransferClaimed(contract, transferHash);
    
    return {
      status: isClaimed ? 'claimed' : 'confirmed',
      confirmed: true,
      blockNumber: receipt.blockNumber,
      gasUsed: receipt.gasUsed.toString(),
    };
  } catch (error) {
    console.error('Error getting transfer status:', error);
    return { status: 'unknown', confirmed: false, error: error.message };
  }
}; 

/**
 * Get claim details for a specific claim number
 * @param {ethers.Contract} contract - Counterstake contract instance
 * @param {number} claimNum - Claim number
 * @param {string} rpcUrl - RPC URL for fallback provider
 * @returns {Promise<Object|null>} Claim details or null if not found
 */
export const getClaimDetails = async (contract, claimNum, rpcUrl) => {
  try {
    // Use the centralized claim details fetcher
    return await fetchClaimDetails({
      contract,
      claimNum,
      rpcUrl,
      useDirectProvider: !!rpcUrl // Use direct provider if RPC URL is provided
    });
  } catch (error) {
    console.error('Error in getClaimDetails:', error);
    return null;
  }
};

/**
 * Get NewClaim events using unified block fetcher (replaces block scanning)
 * @param {ethers.Contract} contract - Counterstake contract instance
 * @param {number} limit - Maximum number of events to fetch (default: 100)
 * @param {string} networkKey - Network key for unified fetcher
 * @returns {Promise<Array>} Array of NewClaim events with txid
 */
export const getNewClaimEvents = async (contract, limit = 100, networkKey = null) => {
  try {
    console.log('🚀 getNewClaimEvents: Using unified block fetcher for contract:', contract.address);
    console.log('🚀 getNewClaimEvents: Parameters - limit:', limit, 'networkKey:', networkKey);
    
    if (!networkKey) {
      console.error('❌ getNewClaimEvents: networkKey is required for unified fetcher');
      return [];
    }
    
    // Import unified fetcher
    const { getNewClaimBlockNumbersUnified } = await import('./unified-block-fetcher.js');
    
    // Get block numbers where NewClaim events occurred
    const result = await getNewClaimBlockNumbersUnified(networkKey, contract.address, {
      limit: limit
    });
    
    console.log('🚀 getNewClaimEvents: Found', result.eventCount, 'NewClaim events in', result.blockNumbers.length, 'blocks');
    
    // Fetch actual event details from provider using block numbers from unified fetcher
    const events = await fetchClaimEventsFromProviderBlocks(
      contract.provider,
      contract.address,
      result.events.slice(0, limit)
    );
    
    console.log('🚀 getNewClaimEvents: Returning', events.length, 'events with actual data from provider');
    
    // If unified fetcher didn't find any events, try fallback approach
    if (events.length === 0) {
      console.log('🔍 getNewClaimEvents: No events found via unified fetcher, trying fallback approach...');
      return await getNewClaimEventsFallback(contract, limit);
    }
    
    return events;
    
  } catch (error) {
    console.error('❌ Error getting NewClaim events with unified fetcher:', error);
    console.log('🔍 getNewClaimEvents: Trying fallback approach due to error...');
    return await getNewClaimEventsFallback(contract, limit);
  }
};

/**
 * Fetch NewClaim event details from provider using block numbers from unified fetcher
 * @param {ethers.providers.Provider} provider - Network provider
 * @param {string} bridgeAddress - Bridge contract address
 * @param {Array} eventBlocks - Array of event block info from unified fetcher
 * @returns {Promise<Array>} Array of decoded NewClaim events
 */
const fetchClaimEventsFromProviderBlocks = async (provider, bridgeAddress, eventBlocks) => {
  try {
    console.log(`🔍 fetchClaimEventsFromProviderBlocks: Processing ${eventBlocks.length} NewClaim events for ${bridgeAddress}`);
    
    // Use COUNTERSTAKE_ABI for NewClaim events
    const contract = createCounterstakeContractFactory(bridgeAddress, provider);
    
    const allEvents = [];
    
    // Get unique block numbers from eventBlocks
    const blockNumbers = [...new Set(eventBlocks.map(event => event.blockNumber))].sort((a, b) => a - b);
    
    console.log(`🔍 Querying ${blockNumbers.length} unique blocks for NewClaim events`);
    
    // Query all blocks at once with a range
    if (blockNumbers.length > 0) {
      const fromBlock = blockNumbers[0];
      const toBlock = blockNumbers[blockNumbers.length - 1];
      
      console.log(`🔍 Querying NewClaim events from block ${fromBlock} to ${toBlock}`);
      
      const filter = contract.filters.NewClaim();
      const events = await contract.queryFilter(filter, fromBlock, toBlock);
      
      console.log(`🔍 Found ${events.length} NewClaim events in range ${fromBlock}-${toBlock}`);
      
      for (const event of events) {
        const decodedEvent = {
          claim_num: event.args.claim_num,
          author_address: event.args.author_address,
          sender_address: event.args.sender_address,
          recipient_address: event.args.recipient_address,
          txid: event.args.txid,
          txts: event.args.txts,
          amount: event.args.amount,
          reward: event.args.reward,
          stake: event.args.stake,
          data: event.args.data,
          expiry_ts: event.args.expiry_ts,
          blockNumber: event.blockNumber,
          transactionHash: event.transactionHash,
          logIndex: event.logIndex
        };
        allEvents.push(decodedEvent);
      }
    }
    
    console.log(`🔍 fetchClaimEventsFromProviderBlocks: Returning ${allEvents.length} decoded NewClaim events`);
    return allEvents;
    
  } catch (error) {
    console.error(`❌ Error in fetchClaimEventsFromProviderBlocks:`, error);
    return [];
  }
};

/**
 * Fallback method to get NewClaim events using direct contract query
 * @param {ethers.Contract} contract - Counterstake contract instance
 * @param {number} limit - Maximum number of events to fetch
 * @returns {Promise<Array>} Array of NewClaim events with txid
 */
const getNewClaimEventsFallback = async (contract, limit = 100) => {
  try {
    console.log('🔍 getNewClaimEventsFallback: Using direct contract query for contract:', contract.address);
    
    // Get the last claim number to determine the range
    const lastClaimNum = await contract.last_claim_num();
    const startClaim = Math.max(1, lastClaimNum.toNumber() - limit + 1);
    const endClaim = lastClaimNum.toNumber();
    
    console.log('🔍 getNewClaimEventsFallback: Checking NewClaim events from', startClaim, 'to', endClaim);
    
    // Get current block number to limit the query range
    const provider = contract.provider;
    const currentBlock = await provider.getBlockNumber();
    
    // Use a much wider block range to capture all NewClaim events for existing claims
    // Query from block 0 to current block to ensure we get all events
    const fromBlock = 0;
    
    console.log('🔍 getNewClaimEventsFallback: Querying events from block', fromBlock, 'to', currentBlock);
    
    try {
      // Try to get recent NewClaim events with block range limit
      const allEvents = await contract.queryFilter(
        contract.filters.NewClaim(),
        fromBlock,
        currentBlock
      );
      
      console.log('🔍 getNewClaimEventsFallback: Found', allEvents.length, 'NewClaim events in recent blocks');
      
      if (allEvents.length > 0) {
        // Sort by claim number and return the most recent ones
        const sortedEvents = allEvents.sort((a, b) => a.args.claim_num.toNumber() - b.args.claim_num.toNumber());
        const recentEvents = sortedEvents.slice(-limit);
        
        const events = [];
        for (const event of recentEvents) {
          const claimNum = event.args.claim_num.toNumber();
          const eventData = {
            claim_num: claimNum,
            author_address: event.args.author_address,
            sender_address: event.args.sender_address,
            recipient_address: event.args.recipient_address,
            txid: event.args.txid,
            txts: event.args.txts,
            amount: event.args.amount,
            reward: event.args.reward,
            stake: event.args.stake,
            data: event.args.data,
            expiry_ts: event.args.expiry_ts,
            blockNumber: event.blockNumber,
            transactionHash: event.transactionHash,
            logIndex: event.logIndex
          };
          events.push(eventData);
          console.log('🔍 getNewClaimEventsFallback: Found NewClaim event for claim', claimNum, 'with txid:', eventData.txid);
        }
        
        console.log('🔍 getNewClaimEventsFallback: Returning', events.length, 'most recent events');
        return events;
      }
      
    } catch (recentError) {
      console.log('🔍 getNewClaimEventsFallback: Recent events query failed:', recentError.message);
    }
    
    // If no events found, return empty array
    console.log('🔍 getNewClaimEventsFallback: No events found');
    return [];
    
  } catch (error) {
    console.error('❌ Error in getNewClaimEventsFallback:', error);
    return [];
  }
};

/**
 * Get all claims for a contract (up to a limit)
 * @param {ethers.Contract} contract - Counterstake contract instance
 * @param {number} limit - Maximum number of claims to fetch (default: 100)
 * @returns {Promise<Array>} Array of claim details
 */
export const getAllClaims = async (contract, limit = 100, rpcUrl, networkKey = null) => {
  try {
    console.log('🔍 getAllClaims: Getting last_claim_num from contract:', contract.address);
    
    // Use the same direct provider approach as the working script
    const directProvider = new ethers.providers.JsonRpcProvider(rpcUrl);
    
    // Create a contract instance with the direct provider for reading
    const directContract = new ethers.Contract(contract.address, contract.interface, directProvider);
    
    const lastClaimNum = await directContract.last_claim_num();
    console.log('🔍 getAllClaims: last_claim_num result:', lastClaimNum.toString());
    
    // Get NewClaim events to extract txid information
    console.log('🚀 getAllClaims: About to call getNewClaimEvents with parameters:', { limit, networkKey });
    const newClaimEvents = await getNewClaimEvents(contract, limit, networkKey);
    console.log('🚀 getAllClaims: getNewClaimEvents returned', newClaimEvents.length, 'events');
    console.log('🔍 getAllClaims: Got NewClaim events:', newClaimEvents.length);
    console.log('🔍 getAllClaims: NewClaim events details:', newClaimEvents.map(event => ({
      claim_num: event.claim_num,
      txid: event.txid,
      hasTxid: !!event.txid
    })));
    
    const claims = [];
    
    // Start from the most recent claims and work backwards
    const startClaim = Math.max(1, lastClaimNum.toNumber() - limit + 1);
    const endClaim = lastClaimNum.toNumber();
    
    console.log('🔍 getAllClaims: Checking claims from', startClaim, 'to', endClaim);
    
    for (let claimNum = endClaim; claimNum >= startClaim; claimNum--) {
      console.log('🔍 getAllClaims: Checking claim number:', claimNum);
      const claimDetails = await getClaimDetails(contract, claimNum, rpcUrl);
      if (claimDetails) {
        console.log('🔍 getAllClaims: Found claim:', claimNum, claimDetails);
        
        // Find the corresponding NewClaim event to get the txid, reward, and blockNumber
        const newClaimEvent = newClaimEvents.find(event => event.claim_num === claimNum);
        console.log('🔍 getAllClaims: Looking for NewClaim event for claim', claimNum, ':', {
          foundEvent: !!newClaimEvent,
          eventDetails: newClaimEvent ? {
            claim_num: newClaimEvent.claim_num,
            txid: newClaimEvent.txid,
            hasTxid: !!newClaimEvent.txid
          } : null,
          availableEvents: newClaimEvents.map(e => ({ claim_num: e.claim_num, hasTxid: !!e.txid }))
        });
        
        let txid = newClaimEvent ? newClaimEvent.txid : null;
        let reward = newClaimEvent ? newClaimEvent.reward : null;
        let blockNumber = newClaimEvent ? newClaimEvent.blockNumber : null;
        let claimTransactionHash = newClaimEvent ? newClaimEvent.transactionHash : null;
        
        // If we couldn't get txid from NewClaim event, try to get it from the claim data itself
        if (!txid && claimDetails.txid) {
          txid = claimDetails.txid;
          console.log('🔍 getAllClaims: Claim', claimNum, 'txid from claim data:', txid);
        } else if (newClaimEvent) {
          console.log('🔍 getAllClaims: Claim', claimNum, 'txid from NewClaim event:', txid);
        } else {
          console.log('🔍 getAllClaims: Claim', claimNum, 'no txid available - no matching NewClaim event found');
        }
        
        // Add the claim number, txid, reward, blockNumber, and claim transaction hash to the claim details
        const claimWithNumber = {
          ...claimDetails,
          claim_num: claimNum,
          txid: txid, // Add the txid from the NewClaim event
          reward: reward, // Add the reward from the NewClaim event
          blockNumber: blockNumber, // Add the blockNumber from the NewClaim event
          claimTransactionHash: claimTransactionHash // Add the transaction hash from the NewClaim event
        };
        console.log('🔍 getAllClaims: Added claim with number, txid, reward, blockNumber, and claim transaction hash:', {
          claimNum: claimNum,
          txid: txid,
          reward: reward,
          blockNumber: blockNumber,
          claimTransactionHash: claimTransactionHash,
          claimWithNumber: claimWithNumber,
          hasClaimNum: !!claimWithNumber.claim_num,
          hasTxid: !!claimWithNumber.txid,
          hasReward: !!claimWithNumber.reward,
          hasBlockNumber: !!claimWithNumber.blockNumber,
          hasClaimTransactionHash: !!claimWithNumber.claimTransactionHash
        });
        claims.push(claimWithNumber);
      } else {
        console.log('🔍 getAllClaims: Claim not found:', claimNum);
      }
    }
    
    console.log('🔍 getAllClaims: Total claims found:', claims.length);
    return claims;
  } catch (error) {
    console.error('❌ Error getting all claims:', error);
    return [];
  }
};

/**
 * Get claims for a specific recipient address
 * @param {ethers.Contract} contract - Counterstake contract instance
 * @param {string} recipientAddress - Recipient address to filter by
 * @param {number} limit - Maximum number of claims to fetch (default: 100)
 * @returns {Promise<Array>} Array of claim details for the recipient
 */
export const getClaimsForRecipient = async (contract, recipientAddress, limit = 100, rpcUrl, networkKey = null) => {
  try {
    const allClaims = await getAllClaims(contract, limit, rpcUrl, networkKey);
    return allClaims.filter(claim => 
      claim.recipient_address.toLowerCase() === recipientAddress.toLowerCase()
    );
  } catch (error) {
    console.error('Error getting claims for recipient:', error);
    return [];
  }
};

/**
 * Create a Counterstake contract instance
 * @param {ethers.providers.Provider|ethers.Signer} providerOrSigner - Provider or signer
 * @param {string} contractAddress - Contract address
 * @returns {Promise<ethers.Contract>} Counterstake contract instance
 */
export const createCounterstakeContract = async (providerOrSigner, contractAddress) => {
  try {
    // Check if provider/signer is valid
    if (!providerOrSigner) {
      throw new Error('Provider or signer is required');
    }
    
    // Check if contract address is valid
    if (!contractAddress || !ethers.utils.isAddress(contractAddress)) {
      throw new Error('Invalid contract address');
    }
    
    // Use factory function for consistent contract creation
    const contract = createCounterstakeContractFactory(contractAddress, providerOrSigner);
    console.log('🔍 COUNTERSTAKE_ABI loaded:', contract ? 'yes' : 'no');
    console.log('🔍 Contract created with address:', contract.address);
    console.log('🔍 Contract interface functions:', Object.keys(contract.interface.functions || {}));
    return contract;
  } catch (error) {
    console.error('Error creating Counterstake contract:', error);
    throw error;
  }
}; 